import React, { useState, memo } from "react";
import {
  Box,
  Typography,
  Avatar,
  Stack,
  Chip,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Tooltip,
  Divider,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Link,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import WarningIcon from "@mui/icons-material/Warning";

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SuperAdminUserCardUnmemoized = ({
  user,
  onView,
  onEdit,
  onDelete,
  isDuplicate,
  providerUsers,
  onProviderChange,
  onProviderToggle,
  fetchedPricelists,
  loadingPricelists,
  fetchPricelistName,
}) => {
  const theme = useTheme();
  const [providerId, setProviderId] = useState(user.createdByUserId || "");

  // Determine user status using subscriptionActive as the source of truth
  const getUserStatus = () => {
    if (user.createdByUserId === "deleted") {
      return { label: "Deleted", color: "error" };
    }
    return {
      label: user.subscriptionActive ? "Active" : "Inactive",
      color: user.subscriptionActive ? "success" : "error",
    };
  };

  const status = getUserStatus();

  const handleProviderChange = (event) => {
    const newProviderId = event.target.value;
    setProviderId(newProviderId);
    if (onProviderChange) {
      onProviderChange(user.id, newProviderId);
    }
  };

  return (
    <Card
      sx={{
        mb: 2,
        border: `2px solid ${isDuplicate ? "#ffc107" : theme.palette.divider}`,
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        backgroundColor: "background.paper",
        "&:hover": {
          boxShadow: theme.shadows[4],
          borderColor: isDuplicate ? "#ffc107" : theme.palette.primary.main,
        },
      }}
    >
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}>
          <Box sx={{ position: "relative", mr: 2 }}>
            <Avatar
              sx={{
                width: 56,
                height: 56,
                bgcolor: isDuplicate ? "#ffc107" : theme.palette.primary.main,
                fontSize: "1.5rem",
                color: isDuplicate ? "#000" : "#fff",
              }}
            >
              {user.username?.charAt(0).toUpperCase() ||
                user.email?.charAt(0).toUpperCase()}
            </Avatar>
            {isDuplicate && (
              <Tooltip title="Duplicate email detected">
                <Chip
                  icon={<WarningIcon sx={{ fontSize: 16 }} />}
                  label="Duplicate"
                  size="small"
                  sx={{
                    position: "absolute",
                    top: -6,
                    right: -6,
                    bgcolor: "#ffc107",
                    color: "#000",
                    fontSize: "0.6rem",
                    fontWeight: 600,
                    minHeight: 20,
                    "& .MuiChip-label": {
                      padding: "0 6px",
                    },
                    "& .MuiChip-icon": {
                      margin: 0,
                    },
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {user.username || "Unnamed User"}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {user.email}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ ml: 1 }}>
            <Chip label={status.label} color={status.color} size="small" />
            <Chip
              label={user.isProvider ? "Provider" : "User"}
              color={user.isProvider ? "info" : "default"}
              size="small"
              variant="outlined"
            />
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Created
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDate(user.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Last Login
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatDate(user.lastLogin)}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Searches Left
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {user.searchesRemaining ?? "Unlimited"}
            </Typography>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Subscription
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {user.subscriptionTier || "N/A"} -{" "}
              {user.subscriptionTerm || "N/A"}
            </Typography>
          </Grid>
<Grid item xs={6} sm={4}>
             <Typography variant="caption" color="text.secondary">
               Active Pricelist
             </Typography>
             <Typography variant="body2" sx={{ fontWeight: 500 }}>
               {user.activePricelistID ? (
                 fetchedPricelists[user.activePricelistID] || (
                   <Link
                     component="button"
                     onClick={() => fetchPricelistName(user.activePricelistID)}
                     sx={{
                       color: "primary.main",
                       textDecoration: "none",
                       "&:hover": { textDecoration: "underline" },
                     }}
                   >
                     {loadingPricelists[user.activePricelistID]
                       ? "Loading..."
                       : "View name"}
                   </Link>
                 )
               ) : (
                 "None"
               )}
             </Typography>
           </Grid>
          <Grid item xs={6} sm={4}>
            <Typography variant="caption" color="text.secondary">
              Account Status
            </Typography>
            <Chip label={status.label} color={status.color} size="small" />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth size="small">
              <InputLabel>Provider</InputLabel>
              <Select
                value={providerId}
                label="Provider"
                onChange={handleProviderChange}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {providerUsers.map((provider) => (
                  <MenuItem key={provider.id} value={provider.id}>
                    {provider.username || provider.email}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={
                    user &&
                    user.isProvider !== undefined &&
                    user.isProvider !== null
                      ? user.isProvider
                      : false
                  }
                  onChange={() => {
                    if (
                      onProviderToggle &&
                      typeof onProviderToggle === "function"
                    ) {
                      onProviderToggle(user);
                    }
                  }}
                />
              }
              label={
                <Typography variant="caption" color="text.secondary">
                  Is Provider
                </Typography>
              }
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Customer's Personal Offset
            </Typography>
            <Chip
              label={
                user.priceOffsetValue && user.priceOffsetValue !== 0
                  ? user.priceOffsetType === "percentage"
                    ? `${user.priceOffsetValue > 0 ? "+" : ""}${user.priceOffsetValue}%`
                    : `$${user.priceOffsetValue > 0 ? "+" : ""}${Math.abs(user.priceOffsetValue).toFixed(2)}`
                  : "No personal offset"
              }
              size="small"
              color={
                user.priceOffsetValue && user.priceOffsetValue > 0
                  ? "warning"
                  : user.priceOffsetValue && user.priceOffsetValue < 0
                    ? "success"
                    : "default"
              }
              sx={{ fontWeight: 500 }}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Provider's Personal Offset
            </Typography>
            <Chip
              label={
                user.providerOffsetValue && user.providerOffsetValue !== 0
                  ? user.providerOffsetType === "percentage"
                    ? `${user.providerOffsetValue > 0 ? "+" : ""}${user.providerOffsetValue}%`
                    : `$${user.providerOffsetValue > 0 ? "+" : ""}${Math.abs(user.providerOffsetValue).toFixed(2)}`
                  : "No personal offset"
              }
              size="small"
              color={
                user.providerOffsetValue && user.providerOffsetValue > 0
                  ? "warning"
                  : user.providerOffsetValue && user.providerOffsetValue < 0
                    ? "success"
                    : "default"
              }
              sx={{ fontWeight: 500 }}
            />
          </Grid>
        </Grid>
      </CardContent>

      <CardActions sx={{ justifyContent: "flex-end", px: 2, pb: 2 }}>
        <Tooltip title="View Details">
          <IconButton onClick={() => onView(user)} color="primary">
            <VisibilityIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit User">
          <IconButton onClick={() => onEdit(user)} color="primary">
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete User">
          <IconButton onClick={() => onDelete(user)} color="error">
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </CardActions>
    </Card>
  );
};

const SuperAdminUserCard = memo(
  SuperAdminUserCardUnmemoized,
  (prevProps, nextProps) => {
    return (
      prevProps.user.id === nextProps.user.id &&
      prevProps.user.isProvider === nextProps.user.isProvider &&
      prevProps.user.subscriptionActive === nextProps.user.subscriptionActive &&
      prevProps.user.createdByUserId === nextProps.user.createdByUserId &&
      prevProps.isDuplicate === nextProps.isDuplicate
    );
  }
);

export default SuperAdminUserCard;
